import React, { useState, useEffect, useLayoutEffect } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { PlaceHolderFormat } from "../../Constant/requireMessage";
import BaseInput from "../../BaseComponents/BaseInput/index";
import {
  Email,
  ForgotPassword,
  Password,
  SignIn,
  login,
  welcomeBack,
  Loading,
} from "../../Constant";
import { emailRegex, validationMessages } from "../../Constant/validation";
import { loginApi, loginStatsApi } from "../../Api/AuthApi";
import { getOnboardingStatusApi } from "../../Api/OrganizationApi";
import { toast } from "react-toastify";
import { StatusCodes } from "http-status-codes";
import { Link, useNavigate } from "react-router-dom";
import BaseButton from "../../BaseComponents/BaseButton";
import {
  All,
  CENTER_IDS,
  CENTER_NAME,
  checkStatusCodeSuccess,
  clearLocalItem,
  DEFAULT_CENTER,
  findPathByPermission,
  getLocalItem,
  getSessionId,
  loginMessage,
  navigateToFirstModule,
  setLocalItem,
} from "../../Constant/common";
import { userRolePermissionApi } from "../../Api/roleApi";
import { centerApi } from "../../Api/common";
import PublicPageLayout from "../../BaseComponents/PublicPageLayout";
import { ArrowRight, Users, Calendar, TrendingUp, Shield, Zap, Award } from "react-feather";
import logoDark from "../../assets/images/logo/dark-based-logo-long.svg";
import logoLight from "../../assets/images/logo/white-based-logo-long.svg";
import logoIconLight from "../../assets/images/logo/light-logo.svg";
import logoIconDark from "../../assets/images/logo/favicon-dark-mode.svg";
import "./login.css";

const Login = () => {
  document.title = login;
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [isDark, setIsDark] = useState(document.documentElement.classList.contains("dark"));
  const [loginStats, setLoginStats] = useState({ center: 0, organization: 0, staff: 0 });

  const features = [
    { icon: Users, title: "Team Management", desc: "Manage your workforce efficiently" },
    { icon: Calendar, title: "Smart Scheduling", desc: "Automated shift planning" },
    { icon: TrendingUp, title: "Analytics", desc: "Real-time insights & reports" },
    { icon: Shield, title: "Secure & Compliant", desc: "Enterprise-grade security" },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [features.length]);

  useEffect(() => {
    // Fetch login statistics
    loginStatsApi()
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setLoginStats({
            center: resp?.data?.center || 0,
            organization: resp?.data?.organization || 0,
            staff: resp?.data?.staff || 0,
          });
        }
      })
      .catch(() => {});
  }, []);

  useLayoutEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  const loginForm = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema: yup.object({
      email: yup
        .string()
        .required(validationMessages.required(Email))
        .matches(emailRegex, validationMessages.format(Email)),
      password: yup.string().required(validationMessages.required(Password)),
    }),
    onSubmit: (values) => {
      setLoader(true);
      loginApi({ email: values.email, password: values.password, platform: "web" })
        .then((resp) => {
          if (
            [StatusCodes.ACCEPTED, StatusCodes.OK, StatusCodes.CREATED].includes(resp.statusCode)
          ) {
            setLocalItem("token", resp?.data?.token);
            getSessionId(resp?.data?.token);
            if (getLocalItem("role")) {
              setLoader(true);
              userRolePermissionApi({ role_name: getLocalItem("role") })
                .then((resp) => {
                  if (checkStatusCodeSuccess(resp.statusCode)) {
                    const permission =
                      resp?.data?.sort((a, b) =>
                        (a.module_name || "").localeCompare(b.module_name || ""),
                      ) || [];
                    const path = findPathByPermission(permission);
                    if (!path) {
                      clearLocalItem();
                      toast.error(loginMessage.accessDeniedNoPermission);
                      setLoader(false);
                      return;
                    }
                    toast.success(loginMessage.success);
                    setLocalItem("permissions", JSON.stringify(permission));
                    centerApi()
                      .then((resp) => {
                        if (checkStatusCodeSuccess(resp.statusCode)) {
                          if (resp?.data && resp.data.length > 0) {
                            setLocalItem("onboarding_center_id", resp.data[0].id);
                            const orgName = resp.data[0]?.organization?.organization_name;
                            if (orgName) {
                              setLocalItem("organization_name", orgName);
                            }
                          }
                          const centers = resp?.data?.map((item) => ({
                            label: String(item?.center_name),
                            value: String(item?.id),
                          }));
                          const centerIds = getLocalItem(CENTER_IDS);
                          if (centerIds !== null) {
                            const parsed = JSON.parse(centerIds);
                            if (Array.isArray(parsed) && parsed.length > 0) {
                              const sel =
                                parsed.length > 1
                                  ? parsed[0] !== All
                                    ? parsed[0]
                                    : null
                                  : parsed[0];
                              setLocalItem(DEFAULT_CENTER, sel);
                              setLocalItem(
                                CENTER_NAME,
                                centers.find((c) => c.value === String(sel))?.label,
                              );
                            }
                          }
                        }
                      })
                      .catch((err) => err)
                      .finally(() => {
                        const role = getLocalItem("role");
                        const organizationId = getLocalItem("organizationId");
                        if (role && role.startsWith("Admin") && organizationId) {
                          getOnboardingStatusApi(organizationId)
                            .then((res) => {
                              if (checkStatusCodeSuccess(res?.statusCode)) {
                                const onboarding = res?.data;
                                if (onboarding && !onboarding.completed) {
                                  navigate(`/onboarding`, { replace: true });
                                } else {
                                  navigateToFirstModule(navigate);
                                }
                              } else {
                                navigateToFirstModule(navigate);
                              }
                            })
                            .catch(() => {
                              navigateToFirstModule(navigate);
                            })
                            .finally(() => {
                              setLoader(false);
                            });
                        } else {
                          setLoader(false);
                          navigateToFirstModule(navigate);
                        }
                      });
                  }
                })
                .catch(() => toast.error(loginMessage.accessDenied))
                .finally(() => setLoader(false));
            }
          } else {
            toast.error(resp?.message);
          }
        })
        .catch((err) => toast.error(err?.response?.data?.message || err?.message))
        .finally(() => setLoader(false));
    },
  });

  return (
    <PublicPageLayout>
      {/* Container with max-width for ultra-wide screens */}
      <div
        style={{
          display: "flex",
          width: "100%",
          maxWidth: "1600px", // Limit width on ultra-wide screens
          margin: "0 auto",
          gap: "40px",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Left panel — branding with animations */}
        <div className="login-left-panel">
          <div className="login-left-content">
            {/* Animated floating elements */}
            <div className="floating-elements">
              <div className="float-element element-1"></div>
              <div className="float-element element-2"></div>
              <div className="float-element element-3"></div>
              <div className="float-element element-4"></div>
            </div>

            {/* Animated title */}
            <img
              src={isDark ? logoDark : logoLight}
              alt="HR Fusions"
              style={{
                height: "56px",
                width: "auto",
                marginBottom: "16px",
                objectFit: "contain",
              }}
            />

            <p className="login-brand-subtitle">
              Streamline your workforce management with intelligent HR tools built for modern teams.
            </p>

            {/* Animated feature cards */}
            <div className="feature-cards-container">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={index}
                    className={`feature-card ${activeFeature === index ? "active" : ""}`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="feature-icon">
                      <Icon size={18} strokeWidth={2} />
                    </div>
                    <div className="feature-content">
                      <h3>{feature.title}</h3>
                      <p>{feature.desc}</p>
                    </div>
                    <div className="feature-shine"></div>
                  </div>
                );
              })}
            </div>

            {/* Stats with count-up animation */}
            <div className="login-stats">
              <div className="stat-item">
                <div className="stat-value">
                  <Award size={16} />
                  <span>{loginStats.organization}</span>
                </div>
                <div className="stat-label">Organizations</div>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <div className="stat-value">
                  <Zap size={16} />
                  <span>{loginStats.center}</span>
                </div>
                <div className="stat-label">Centers</div>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <div className="stat-value">
                  <Users size={16} />
                  <span>{loginStats.staff}</span>
                </div>
                <div className="stat-label">Staff</div>
              </div>
            </div>

            {/* Animated wave decoration */}
            <div className="wave-decoration">
              <svg viewBox="0 0 1200 120" preserveAspectRatio="none">
                <path d="M0,0 C300,80 600,80 900,0 L900,120 L0,120 Z" className="wave-path"></path>
              </svg>
            </div>
          </div>
        </div>

        {/* Right panel — form */}
        <div
          style={{
            width: "100%",
            maxWidth: 480,
            margin: "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px 20px",
          }}
        >
          <div
            className="animate-scale-in"
            style={{
              width: "100%",
              background: "var(--glass-bg)",
              border: "1px solid var(--glass-border)",
              borderRadius: 24,
              padding: "40px 36px",
              boxShadow: "0 32px 80px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.05)",
              backdropFilter: "blur(24px) saturate(180%)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Shimmer */}
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                top: 0,
                left: "-60%",
                width: "40%",
                height: "100%",
                background: "linear-gradient(90deg,transparent,rgba(255,255,255,0.03),transparent)",
                animation: "beam 5s ease-in-out infinite",
                pointerEvents: "none",
              }}
            />

            {/* Header */}
            <div style={{ marginBottom: 32 }}>
              <div
                style={{
                  width: 65,
                  height: 65,
                  borderRadius: 20,
                  marginBottom: 24,
                  background: "rgba(255, 255, 255, 0.08)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                  padding: "8px",
                }}
              >
                <img
                  src={isDark ? logoIconDark : logoIconLight}
                  alt="HR Fusions"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </div>
              <h2 style={{ fontSize: "1.5rem", fontWeight: 800, marginBottom: 6 }}>Welcome back</h2>
              <p style={{ color: "var(--text-muted)", fontSize: "0.875rem", margin: 0 }}>
                {welcomeBack}
              </p>
            </div>

            {/* Form */}
            <form onSubmit={loginForm.handleSubmit} noValidate>
              <BaseInput
                name="email"
                type="email"
                label={Email}
                placeholder={PlaceHolderFormat(Email)}
                handleChange={loginForm.handleChange}
                handleBlur={loginForm.handleBlur}
                value={loginForm.values.email}
                touched={loginForm.touched.email}
                error={loginForm.errors.email}
              />

              <BaseInput
                name="password"
                type="password"
                label={Password}
                placeholder={PlaceHolderFormat(Password)}
                handleChange={loginForm.handleChange}
                handleBlur={loginForm.handleBlur}
                value={loginForm.values.password}
                touched={loginForm.touched.password}
                error={loginForm.errors.password}
              />

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: -8,
                  marginBottom: 24,
                }}
              >
                <Link
                  to="/forgotPassword"
                  style={{ fontSize: "0.8125rem", color: "var(--brand-indigo)", fontWeight: 500 }}
                >
                  {ForgotPassword}?
                </Link>
              </div>

              <BaseButton
                type="submit"
                color="primary"
                disabled={loader}
                loader={loader}
                style={{
                  width: "100%",
                  padding: "11px 20px",
                  fontSize: "0.9375rem",
                  justifyContent: "center",
                }}
              >
                {loader ? (
                  Loading
                ) : (
                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {SignIn} <ArrowRight size={16} strokeWidth={2} />
                  </span>
                )}
              </BaseButton>
            </form>
          </div>
        </div>
      </div>
    </PublicPageLayout>
  );
};

export default Login;
